import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button } from "@mui/material";
import LightOnlyPage from "../../components/LightOnlyPage";
import { useNavigate, useOutletContext } from "react-router-dom";
import OnboardingChat from "./chat/OnboardingChat";
import { createWorkspaceApi } from "../../api/workspaceApi";
import { completeOnboardingApi } from "../../api/userApi";
import { useAuth } from "../../components/AuthProvider";
import { errorDetail } from "../../helper/errorDetail";
import {
  clearPostLoginRedirect,
  clearPendingOnboardingCompletion,
  persistOnboardingSearch,
  persistPendingOnboardingCompletion,
  readPendingOnboardingCompletion,
} from "../../helper/onboardingHandoff";
import { startOnboardingActionRun } from "../../helper/onboardingActions";
import { suggestSearchName } from "../../helper/suggestSearchName";
import { resolveIndustryInclude } from "./onboardingShared";

const GENERIC_SAVE_ERROR = "An unexpected error occurred. Please try again.";
const LIMIT_SAVE_ERROR =
  "You have reached the saved search limit. Delete an existing one to create another.";
const DUPLICATE_SAVE_ERROR = "A saved search with this name already exists.";
const INVALID_SAVE_ERROR = "Invalid search configuration provided.";
const SESSION_REQUIRED_ERROR = "Please sign in again to create this saved search.";
const COMPLETION_RETRY_DELAY_MS = 250;
const noop = () => undefined;

const wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

async function recordOnboardingCompletion(payload) {
  let lastFailure;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await completeOnboardingApi(payload);
      if (response?.status >= 200 && response.status < 300) return true;
      lastFailure = response;
    } catch (error) {
      lastFailure = error;
    }

    if (attempt === 0) await wait(COMPLETION_RETRY_DELAY_MS);
  }

  console.error(
    "Onboarding completion could not be recorded after retry.",
    lastFailure
  );
  return false;
}

const initialAnswers = () => ({
  industryInclude: [],
  otherIndustries: [],
  otherSelected: false,
  locations: [],
  revenueLowerBound: null,
  revenueUpperBound: null,
  selectedActions: [],
  crmPreference: null,
  searchName: "",
});

function workspaceId(data) {
  return data?.id || data?._id || data?.workspace_id || null;
}

function completionError(response) {
  if (response?.status === 403) {
    return errorDetail(response?.data?.detail, LIMIT_SAVE_ERROR);
  }
  if (response?.status === 409) {
    return errorDetail(response?.data?.detail, DUPLICATE_SAVE_ERROR);
  }
  if (
    response?.status === 422 &&
    errorDetail(response?.data?.detail, "").includes("returned no companies")
  ) {
    return "This query returned no companies. Please broaden your search and try again.";
  }
  if (response?.status === 422) {
    return errorDetail(response?.data?.detail, INVALID_SAVE_ERROR);
  }
  return errorDetail(response?.data?.detail, GENERIC_SAVE_ERROR);
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { beginOnboardingExit = noop } = useOutletContext() || {};
  const { token, userEmail } = useAuth();
  const pageActiveRef = useRef(true);
  const completionInFlight = useRef(false);
  const createdWorkspaceRef = useRef(null);
  const [answers, setAnswers] = useState(initialAnswers);
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState("");
  const [skipFailed, setSkipFailed] = useState(false);

  useEffect(() => {
    pageActiveRef.current = true;
    return () => {
      pageActiveRef.current = false;
    };
  }, []);

  const landOnSavedSearch = useCallback((savedAnswers) => {
    clearPendingOnboardingCompletion(userEmail);
    if (savedAnswers.selected_actions?.length) startOnboardingActionRun(savedAnswers.workspace_id);
    clearPostLoginRedirect();
    beginOnboardingExit();
    navigate(`/workspaces/${savedAnswers.workspace_id}`, { replace: true });
  }, [beginOnboardingExit, navigate, userEmail]);

  useEffect(() => {
    if (!token || !userEmail || completionInFlight.current) return;
    const pending = readPendingOnboardingCompletion(userEmail);
    if (!pending) return;
    createdWorkspaceRef.current = { id: pending.workspace_id, answers: pending };
    completionInFlight.current = true;
    setIsCreating(true);
    void recordOnboardingCompletion({ answers: pending }).then((recorded) => {
      if (!pageActiveRef.current) return;
      completionInFlight.current = false;
      setIsCreating(false);
      if (!recorded) {
        setCreationError("Saved search created, but onboarding could not be finished. Retry finish to continue.");
        return;
      }
      landOnSavedSearch(pending);
    });
  }, [landOnSavedSearch, token, userEmail]);

  const finishOnboarding = useCallback(
    async (overrideAnswers) => {
      if (completionInFlight.current) return;

      const finalAnswers = overrideAnswers || answers;
      const industries = resolveIndustryInclude(
        finalAnswers.industryInclude,
        finalAnswers.otherSelected,
        finalAnswers.otherIndustries
      );
      const suggestion = suggestSearchName({
        industryInclude: industries,
        locations: finalAnswers.locations,
      });
      const name = (finalAnswers.searchName || "").trim() || suggestion;
      setCreationError("");

      // Preserve answers across an expired session before attempting the write.
      const { query } = persistOnboardingSearch({
        industryInclude: industries,
        locations: finalAnswers.locations,
        revenueLowerBound: finalAnswers.revenueLowerBound,
        revenueUpperBound: finalAnswers.revenueUpperBound,
        searchName: name,
      });

      if (!token) {
        setCreationError(SESSION_REQUIRED_ERROR);
        return;
      }

      completionInFlight.current = true;
      setIsCreating(true);
      try {
        if (!createdWorkspaceRef.current) {
          const response = await createWorkspaceApi({ name, query });
          if (!pageActiveRef.current) return;
          const createdId = workspaceId(response?.data);
          if ((response?.status !== 200 && response?.status !== 201) || !createdId) {
            setCreationError(completionError(response));
            return;
          }
          createdWorkspaceRef.current = {
            id: createdId,
            answers: {
              search_name: name,
              query,
              selected_actions: finalAnswers.selectedActions || [],
              workspace_id: createdId,
            },
          };
          persistPendingOnboardingCompletion(userEmail, createdWorkspaceRef.current.answers);
        }
        const recorded = await recordOnboardingCompletion({
          answers: createdWorkspaceRef.current.answers,
        });
        if (!pageActiveRef.current) return;
        if (!recorded) {
          setCreationError("Saved search created, but onboarding could not be finished. Retry finish to continue.");
          return;
        }
        landOnSavedSearch(createdWorkspaceRef.current.answers);
      } catch (error) {
        if (!pageActiveRef.current) return;
        setCreationError(errorDetail(error?.response?.data?.detail, GENERIC_SAVE_ERROR));
      } finally {
        if (pageActiveRef.current) {
          completionInFlight.current = false;
          setIsCreating(false);
        }
      }
    },
    [answers, landOnSavedSearch, token, userEmail]
  );

  const handleSkip = useCallback(async () => {
    if (!pageActiveRef.current || completionInFlight.current) return;
    if (createdWorkspaceRef.current) {
      setCreationError("Saved search created, but onboarding could not be finished. Retry finish to continue.");
      return;
    }
    completionInFlight.current = true;
    setIsCreating(true);
    setCreationError("");
    try {
      const recorded = await recordOnboardingCompletion();
      if (!pageActiveRef.current) return;
      if (!recorded) {
        setSkipFailed(true);
        setCreationError("Could not skip onboarding. Retry skip to continue.");
        return;
      }
      setSkipFailed(false);
      clearPostLoginRedirect();
      beginOnboardingExit();
      navigate("/lists/companies/", { replace: true });
    } finally {
      if (pageActiveRef.current) {
        completionInFlight.current = false;
        setIsCreating(false);
      }
    }
  }, [beginOnboardingExit, navigate]);

  return (
    <LightOnlyPage
      data-testid="onboarding-page"
      sx={{ bgcolor: "#FFFFFF", position: "relative" }}
    >
      {creationError ? (
        <Alert
          severity="error"
          action={createdWorkspaceRef.current ? (
            <Button color="inherit" size="small" onClick={() => finishOnboarding()} disabled={isCreating}>
              Retry finish
            </Button>
          ) : skipFailed ? (
            <Button color="inherit" size="small" onClick={handleSkip} disabled={isCreating}>
              Retry skip
            </Button>
          ) : null}
          sx={{ position: "absolute", zIndex: 4, top: 64, left: "50%", transform: "translateX(-50%)" }}
        >
          {creationError}
        </Alert>
      ) : null}
      <OnboardingChat
        key="onboarding-chat"
        answers={answers}
        onAnswersChange={setAnswers}
        onComplete={finishOnboarding}
        onSkip={handleSkip}
        completionPending={isCreating}
      />
    </LightOnlyPage>
  );
}
